import { Link } from "react-router-dom";
import { useEffect, useState, useCallback, Fragment } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Layout } from "@/components/Layout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SearchableSelect } from "@/components/SearchableSelect";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { UserPlus, MagnifyingGlass, IdentificationBadge, FilePdf, Certificate, DownloadSimple } from "@phosphor-icons/react";
import { ROLE_LABELS as ROLE_LABELS_MAP } from "@/lib/constants";
import { toast } from "sonner";

export default function Staff({ embedded = false }) {
  const { user } = useAuth();
  const Wrap = embedded ? Fragment : Layout;
  const [opts, setOpts] = useState({ roles: [], regions: [], chapters: [], national_slots: [] });
  const [managers, setManagers] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // dialog form
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(null);
  const [role, setRole] = useState("");
  const [slot, setSlot] = useState("");
  const [region, setRegion] = useState("");
  const [chapter, setChapter] = useState("");
  const [facility, setFacility] = useState("");
  const [facOptions, setFacOptions] = useState([]);
  const isNat = ["national_manager", "super_admin"].includes(user.role);
  const LETTER_ROLES = ["regional_manager", "chapter_manager", "facility_manager"];

  const downloadLetter = async (m) => {
    try {
      const res = await api.get(`/managers/${m.id}/appointment-letter`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `appointment_letter_${m.staff_id}.pdf`; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Appointment letter downloaded");
    } catch { toast.error("Could not generate the appointment letter"); }
  };
  const resendLetter = async (m) => {
    try {
      await api.post(`/managers/${m.id}/resend-letter`);
      toast.success(`Appointment letter emailed to ${m.name || m.staff_id}`);
      loadManagers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Could not send the email"); }
  };
  const exportRoster = async () => {
    try {
      const res = await api.get("/export/managers", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url;
      a.download = `staff_roster_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("Staff roster exported");
    } catch { toast.error("Could not export the roster"); }
  };
  const letterStatusPill = (m) => {
    const s = m.letter_email_status;
    if (!s) return null;
    const map = {
      sent: ["Emailed", "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300"],
      failed: ["Email failed", "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300"],
      skipped: ["No email", "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300"],
    };
    const [label, cls] = map[s] || [s, "bg-muted text-muted-foreground"];
    return <span className={`inline-block rounded-full px-2 py-0.5 text-[11px] font-medium ${cls}`} data-testid={`letter-status-${m.staff_id}`}>{label}</span>;
  };

  const loadManagers = useCallback(() => {
    if (isNat) api.get("/managers").then((r) => setManagers(r.data)).catch(() => {});
  }, [isNat]);

  useEffect(() => {
    api.get("/roles/options").then((r) => setOpts(r.data)).catch(() => {});
    loadManagers();
  }, [loadManagers]);

  useEffect(() => {
    if (!q || q.length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      api.get("/members", { params: { search: q, page_size: 8 } }).then((r) => setResults(r.data.items)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    if (role === "facility_manager" && region) {
      api.get("/roles/facilities", { params: { region_name: region } }).then((r) => setFacOptions(r.data.facilities)).catch(() => {});
    }
  }, [role, region]);

  const reset = () => {
    setQ(""); setResults([]); setPicked(null); setRole(""); setSlot("");
    setRegion(""); setChapter(""); setFacility(""); setFacOptions([]);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const chObj = opts.chapters.find((c) => c.code === chapter);
      await api.post("/roles/assign", {
        member_id: picked.id, role,
        slot: slot ? Number(slot) : undefined,
        region_name: region || undefined,
        chapter_code: chapter || undefined,
        facility_name: facility || undefined,
        district: undefined,
      });
      toast.success(`Assigned ${ROLE_LABELS_MAP[role]} to ${picked.first_name} ${picked.last_name}`);
      setOpen(false); reset(); loadManagers();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const unassign = async (m) => {
    if (!window.confirm(`Revoke ${ROLE_LABELS_MAP[m.role]} from ${m.name}? They return to the members directory.`)) return;
    try {
      await api.post("/roles/unassign", { member_id: m.id, role: m.role });
      toast.success(`${m.name} returned to members`);
      loadManagers();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const canSubmit = picked && role && (
    (role === "national_manager" && slot) ||
    (role === "regional_manager" && region) ||
    (role === "chapter_manager" && chapter) ||
    (role === "facility_manager" && facility)
  );

  return (
    <Wrap>
      <div className="animate-fade-up" data-testid="staff-roles-panel">
        <div className={`flex items-start flex-wrap gap-3 ${embedded ? "justify-end" : "justify-between"}`}>
          {!embedded && <div>
            <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Administration</span>
            <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Staff & Roles</h1>
            <p className="text-sm text-muted-foreground mt-2">
              Assign management roles to existing members within your purview.
            </p>
          </div>}
          <div className="flex flex-wrap gap-2">
            {isNat && (
              <Button variant="outline" onClick={exportRoster} data-testid="export-roster-button" className="gap-2">
                <DownloadSimple size={18} weight="bold" /> Export roster
              </Button>
            )}
            {isNat && (
              <Button variant="outline" asChild className="gap-2">
                <Link to="/organization?section=templates&tpl=appointments" data-testid="letter-settings-button"><Certificate size={18} weight="bold" /> Templates</Link>
              </Button>
            )}
            <Button onClick={() => { reset(); setOpen(true); }} data-testid="assign-role-button" className="gap-2">
              <UserPlus size={18} weight="bold" /> Assign role
            </Button>
          </div>
        </div>

        {!isNat && (
          <p className="mt-6 rounded-md border border-dashed border-border p-6 text-sm text-muted-foreground" data-testid="staff-regional-hint">
            Use Assign role to appoint Chapter Secretaries and Facility Representatives from members in your region. Appointed officers stay in the Members directory.
          </p>
        )}

        {isNat && (
          <>
          {/* Mobile cards */}
          <div className="mt-6 space-y-3 md:hidden" data-testid="managers-mobile-list">
            {managers.length === 0 ? (
              <Card className="p-6 text-center text-muted-foreground">No managers assigned yet.</Card>
            ) : managers.map((m) => (
              <Card key={m.id} data-testid={`manager-card-${m.staff_id}`} className="p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-medium truncate">{m.name}</p>
                    <p className="text-xs text-muted-foreground font-mono">{m.staff_id}</p>
                    <span className="inline-block mt-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary">{ROLE_LABELS_MAP[m.role]}</span>
                    <p className="text-sm text-muted-foreground mt-1.5">{m.scope}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => unassign(m)} data-testid={`unassign-mobile-${m.staff_id}`}
                    className="text-destructive hover:bg-destructive hover:text-destructive-foreground shrink-0">Unassign</Button>
                </div>
                {LETTER_ROLES.includes(m.role) && (
                  <div className="mt-3 space-y-2">
                    {letterStatusPill(m)}
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={() => downloadLetter(m)} data-testid={`letter-mobile-${m.staff_id}`}
                        className="flex-1 gap-2"><FilePdf size={16} weight="bold" /> Letter</Button>
                      <Button variant="outline" size="sm" onClick={() => resendLetter(m)} data-testid={`resend-letter-mobile-${m.staff_id}`}
                        className="flex-1">Resend email</Button>
                    </div>
                  </div>
                )}
              </Card>
            ))}
          </div>
          <Card className="mt-6 border-border overflow-hidden hidden md:block">
            <div className="overflow-auto max-h-[calc(100vh-320px)]">
              <Table>
                <TableHeader className="sticky top-0 z-10 bg-card shadow-[0_1px_0_0_hsl(var(--border))]">
                  <TableRow className="hover:bg-transparent">
                    <TableHead>Name</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Assignment</TableHead>
                    <TableHead className="hidden md:table-cell">Assigned By</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {managers.length === 0 ? (
                    <TableRow><TableCell colSpan={5} className="text-center py-10 text-muted-foreground">No managers assigned yet.</TableCell></TableRow>
                  ) : managers.map((m) => (
                    <TableRow key={m.id} data-testid={`manager-row-${m.staff_id}`}>
                      <TableCell>
                        <p className="font-medium">{m.name}</p>
                        <p className="text-xs text-muted-foreground font-mono">{m.staff_id}</p>
                      </TableCell>
                      <TableCell><span className="text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary">{ROLE_LABELS_MAP[m.role]}</span></TableCell>
                      <TableCell className="text-sm">{m.scope}</TableCell>
                      <TableCell className="hidden md:table-cell text-sm text-muted-foreground font-mono">{m.assigned_by_staff_id || "—"}</TableCell>
                      <TableCell className="text-right">
                        {LETTER_ROLES.includes(m.role) && (
                          <span className="inline-flex items-center gap-2 mr-1 align-middle">
                            {letterStatusPill(m)}
                            <Button variant="ghost" size="sm" onClick={() => downloadLetter(m)} data-testid={`letter-${m.staff_id}`}
                              className="gap-1.5 text-primary hover:bg-primary hover:text-primary-foreground"><FilePdf size={15} weight="bold" /> Letter</Button>
                            <Button variant="ghost" size="sm" onClick={() => resendLetter(m)} data-testid={`resend-letter-${m.staff_id}`}
                              className="gap-1.5">Resend</Button>
                          </span>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => unassign(m)} data-testid={`unassign-${m.staff_id}`}
                          className="text-destructive hover:bg-destructive hover:text-destructive-foreground">Unassign</Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </Card>
          </>
        )}
      </div>

      <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); setOpen(o); }}>
        <DialogContent className="max-w-lg" data-testid="assign-role-dialog">
          <DialogHeader><DialogTitle className="font-display text-xl">Assign management role</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* member search */}
            <div className="space-y-1.5">
              <Label>Find member</Label>
              {picked ? (
                <div className="flex items-center justify-between rounded-md border border-border p-3" data-testid="picked-member">
                  <div>
                    <p className="font-medium">{picked.first_name} {picked.last_name} {picked.staff_id && <span className="text-xs text-muted-foreground font-mono">({picked.staff_id})</span>}</p>
                    <p className="text-xs text-muted-foreground">{picked.email || "no email on file"}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setPicked(null)}>Change</Button>
                </div>
              ) : (
                <>
                  <div className="relative">
                    <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input data-testid="member-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or staff ID…" className="pl-10" />
                  </div>
                  {results.length > 0 && (
                    <div className="border border-border rounded-md divide-y divide-border max-h-52 overflow-auto">
                      {results.map((r) => (
                        <button key={r.id} data-testid={`member-result-${r.staff_id || r.id}`} onClick={() => { setPicked(r); setResults([]); setQ(""); }}
                          className="w-full text-left px-3 py-2 hover:bg-muted transition-colors">
                          <p className="text-sm font-medium">{r.first_name} {r.last_name} <span className="text-xs text-muted-foreground font-mono">{r.staff_id}</span></p>
                          <p className="text-xs text-muted-foreground">{r.facility_name || r.region_name || ""}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* role */}
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => { setRole(v); setSlot(""); setRegion(""); setChapter(""); setFacility(""); }}>
                <SelectTrigger data-testid="role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  {opts.roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS_MAP[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* scope */}
            {role === "national_manager" && (
              <div className="space-y-1.5">
                <Label>National Manager slot</Label>
                <Select value={slot} onValueChange={setSlot}>
                  <SelectTrigger data-testid="slot-select"><SelectValue placeholder="Select slot" /></SelectTrigger>
                  <SelectContent>{opts.national_slots.map((s) => <SelectItem key={s} value={String(s)}>National Manager {s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {role === "regional_manager" && (
              <div className="space-y-1.5">
                <Label>Region</Label>
                <SearchableSelect testId="region-scope" placeholder="Select region" allLabel="—" className="w-full"
                  value={region} options={opts.regions} onChange={setRegion} />
              </div>
            )}
            {role === "chapter_manager" && (
              <div className="space-y-1.5">
                <Label>Chapter</Label>
                <SearchableSelect testId="chapter-scope" placeholder="Select chapter" allLabel="—" className="w-full"
                  value={chapter} options={opts.chapters.map((c) => c.code)} onChange={setChapter} />
              </div>
            )}
            {role === "facility_manager" && (
              <>
                <div className="space-y-1.5">
                  <Label>Region</Label>
                  <SearchableSelect testId="region-scope" placeholder="Select region" allLabel="—" className="w-full"
                    value={region} options={opts.regions} onChange={(v) => { setRegion(v); setFacility(""); }} />
                </div>
                <div className="space-y-1.5">
                  <Label>Facility</Label>
                  <SearchableSelect testId="facility-scope" placeholder={region ? "Select facility" : "Choose region first"} allLabel="—"
                    className="w-full" value={facility} options={facOptions} disabled={!region} onChange={setFacility} />
                </div>
              </>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setOpen(false); reset(); }} disabled={busy}>Cancel</Button>
              <Button onClick={submit} disabled={!canSubmit || busy} data-testid="confirm-assign-button" className="gap-2">
                <IdentificationBadge size={16} weight="bold" /> {busy ? "Assigning…" : "Assign role"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </Wrap>
  );
}
